import type { ChainId } from '@morpho-org/blue-sdk'
import type { RangeValue, SortByOption } from '@morpho-org/ui-v6/client'

import {
  createSearchParamsCache,
  parseAsArrayOf,
  parseAsFloat,
  parseAsInteger,
  parseAsString
} from 'nuqs/server'

import type { TableSettingsState } from '@/helpers/cookieStorage/userSettings/tableSettings/shared'
import type { MarketListInput } from '@/lib/procedures/marketList.contract'

import { AVG_APY_DURATION_LABEL } from '@/constants/avgApy'

export interface MarketsFilters {
  search: string
  chains: ChainId[]
  collateralAssetIdsFilter: string[]
  loanAssetIdsFilter: string[]
  curators: string[]
  sizeUsd_gte: number | null
  sizeUsd_lte: number | null
  totalLiquidityUsd_gte: number | null
  totalLiquidityUsd_lte: number | null
  avgNetBorrowApy_gte: number | null
  avgNetBorrowApy_lte: number | null
  lltv_gte: number | null
  lltv_lte: number | null
}

export interface MarketsMasterFilterState {
  chains: ChainId[]
  collateralAssets: string[]
  loanAssets: string[]
  curators: string[]
  totalMarketSize: RangeValue
  liquidity: RangeValue
  rate: RangeValue
  lltv: RangeValue
}

export const DEFAULT_MARKETS_MASTER_FILTER_STATE: MarketsMasterFilterState = {
  chains: [],
  collateralAssets: [],
  loanAssets: [],
  curators: [],
  totalMarketSize: {},
  liquidity: {},
  rate: {},
  lltv: {}
}

// Keys promoted to the toolbar — excluded from the master filter badge count
// and from analytics active_count so events stay comparable across triggers.
export const MARKETS_MASTER_FILTER_EXCLUDE_KEYS: (keyof MarketsMasterFilterState)[] = [
  'chains',
  'collateralAssets',
  'loanAssets'
]

export const COLUMN_IDS = {
  Network: 'network',
  CollateralAssetSymbol: 'collateralAssetSymbol',
  LoanAssetSymbol: 'loanAssetSymbol',
  Lltv: 'lltv',
  SizeUsd: 'sizeUsd',
  TotalLiquidityUsd: 'totalLiquidityUsd',
  AvgNetBorrowApy: 'avgNetBorrowApy',
  NetSupplyApy: 'netSupplyApy',
  TrustedBy: 'trustedBy',
  Utilization: 'utilization',
  LiquidityAssets: 'liquidityAssets',
  CollateralAssetsUsd: 'collateralAssetsUsd',
  NetBorrowApy: 'netBorrowApy',
  WeeklyBorrowApy: 'weeklyBorrowApy',
  SupplyAssetsUsd: 'supplyAssetsUsd',
  BorrowAssetsUsd: 'borrowAssetsUsd',
  OraclePrice: 'oraclePrice',
  IrmAddress: 'irmAddress',
  BorrowToTarget: 'borrowToTarget',
  ApyAtTarget: 'apyAtTarget',
  UniqueKey: 'uniqueKey',
  BadDebt: 'badDebt',
  RealizedBadDebt: 'realizedBadDebt'
} as const

// Column widths in px, shared by the loaded table and its server skeleton
export const COLUMN_SIZES: Record<(typeof COLUMN_IDS)[keyof typeof COLUMN_IDS], number> = {
  [COLUMN_IDS.Network]: 72,
  [COLUMN_IDS.CollateralAssetSymbol]: 242,
  [COLUMN_IDS.LoanAssetSymbol]: 148,
  [COLUMN_IDS.Lltv]: 88,
  [COLUMN_IDS.SizeUsd]: 140,
  [COLUMN_IDS.TotalLiquidityUsd]: 140,
  [COLUMN_IDS.AvgNetBorrowApy]: 150,
  [COLUMN_IDS.NetSupplyApy]: 140,
  [COLUMN_IDS.TrustedBy]: 100,
  [COLUMN_IDS.Utilization]: 100,
  [COLUMN_IDS.LiquidityAssets]: 160,
  [COLUMN_IDS.CollateralAssetsUsd]: 160,
  [COLUMN_IDS.NetBorrowApy]: 140,
  [COLUMN_IDS.WeeklyBorrowApy]: 140,
  [COLUMN_IDS.SupplyAssetsUsd]: 160,
  [COLUMN_IDS.BorrowAssetsUsd]: 160,
  [COLUMN_IDS.OraclePrice]: 148,
  [COLUMN_IDS.IrmAddress]: 200,
  [COLUMN_IDS.BorrowToTarget]: 128,
  [COLUMN_IDS.ApyAtTarget]: 112,
  [COLUMN_IDS.UniqueKey]: 200,
  [COLUMN_IDS.BadDebt]: 160,
  [COLUMN_IDS.RealizedBadDebt]: 160
}

export type MarketOrderBy = NonNullable<MarketListInput['orderBy']>

export const COLUMN_ID_TO_ORDER_BY: Record<string, MarketOrderBy> = {
  [COLUMN_IDS.CollateralAssetSymbol]: 'collateralAssetSymbol',
  [COLUMN_IDS.LoanAssetSymbol]: 'loanAssetSymbol',
  [COLUMN_IDS.Lltv]: 'lltv',
  [COLUMN_IDS.SizeUsd]: 'sizeUsd',
  [COLUMN_IDS.TotalLiquidityUsd]: 'totalLiquidityUsd',
  [COLUMN_IDS.AvgNetBorrowApy]: 'avgNetBorrowApy',
  [COLUMN_IDS.NetSupplyApy]: 'avgNetSupplyApy',
  [COLUMN_IDS.Utilization]: 'utilization',
  [COLUMN_IDS.LiquidityAssets]: 'liquidityAssetsUsd',
  [COLUMN_IDS.CollateralAssetsUsd]: 'collateralAssetsUsd',
  [COLUMN_IDS.NetBorrowApy]: 'netBorrowApy',
  [COLUMN_IDS.WeeklyBorrowApy]: 'weeklyBorrowApy',
  [COLUMN_IDS.SupplyAssetsUsd]: 'supplyAssetsUsd',
  [COLUMN_IDS.BorrowAssetsUsd]: 'borrowAssetsUsd',
  [COLUMN_IDS.OraclePrice]: 'oraclePrice',
  [COLUMN_IDS.BorrowToTarget]: 'borrowToTargetUsd',
  [COLUMN_IDS.ApyAtTarget]: 'apyAtTarget',
  [COLUMN_IDS.BadDebt]: 'badDebtUsd',
  [COLUMN_IDS.RealizedBadDebt]: 'realizedBadDebtUsd'
}

export const DEFAULT_ORDER_BY = 'borrowAssetsUsd' as const satisfies MarketOrderBy

export const ROW_HEIGHT_ESTIMATE = 64
export const VIRTUALIZER_OVERSCAN = 10

export const MARKETS_PAGE_SIZE = 35

/** Above 0 so the server prefetch is still fresh when the client hydrates it. */
export const USER_MARKET_POSITIONS_STALE_TIME = 60 * 1000

/** Lowercased: the bridge returns market ids as stored, so their casing can differ from the market list's. */
export const userMarketPositionKey = (chainId: number, marketId: string) =>
  `${chainId}:${marketId.toLowerCase()}`

// Rect assumed before the scroll container is measured (SSR + first client
// render), so the virtualizer emits the first page instead of an empty body.
export const VIRTUALIZER_INITIAL_RECT = {
  width: 0,
  height: MARKETS_PAGE_SIZE * ROW_HEIGHT_ESTIMATE
}

export const MARKETS_COLUMN_LABELS: Record<string, string> = {
  [COLUMN_IDS.Network]: 'Network',
  [COLUMN_IDS.LoanAssetSymbol]: 'Loan',
  [COLUMN_IDS.CollateralAssetSymbol]: 'Collateral',
  [COLUMN_IDS.Lltv]: 'LLTV',
  [COLUMN_IDS.SizeUsd]: 'Total Market Size',
  [COLUMN_IDS.TotalLiquidityUsd]: 'Total Liquidity',
  [COLUMN_IDS.AvgNetBorrowApy]: `Borrow Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`,
  [COLUMN_IDS.NetSupplyApy]: `Lend Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`,
  [COLUMN_IDS.TrustedBy]: 'Trusted by',
  [COLUMN_IDS.Utilization]: 'Utilization',
  [COLUMN_IDS.LiquidityAssets]: 'Market Liquidity',
  [COLUMN_IDS.CollateralAssetsUsd]: 'Total Collateral',
  [COLUMN_IDS.NetBorrowApy]: 'Instant Rate',
  [COLUMN_IDS.WeeklyBorrowApy]: '7D Rate',
  [COLUMN_IDS.SupplyAssetsUsd]: 'Total Supply',
  [COLUMN_IDS.BorrowAssetsUsd]: 'Outstanding Loans',
  [COLUMN_IDS.OraclePrice]: 'Collateral Price',
  [COLUMN_IDS.IrmAddress]: 'IRM',
  [COLUMN_IDS.BorrowToTarget]: 'Borrow to Target',
  [COLUMN_IDS.ApyAtTarget]: 'Rate at Target',
  [COLUMN_IDS.UniqueKey]: 'Market ID',
  [COLUMN_IDS.BadDebt]: 'Bad Debt',
  [COLUMN_IDS.RealizedBadDebt]: 'Realized Bad Debt'
}

// By default, all columns are visible unless specified otherwise in this object.
// Hidden by default: advanced/detailed columns that most users don't need at first glance.
export const MARKETS_INITIAL_COLUMNS_VISIBILITY: Record<string, boolean> = {
  [COLUMN_IDS.Utilization]: false,
  [COLUMN_IDS.LiquidityAssets]: false,
  [COLUMN_IDS.CollateralAssetsUsd]: false,
  [COLUMN_IDS.NetBorrowApy]: false,
  [COLUMN_IDS.WeeklyBorrowApy]: false,
  [COLUMN_IDS.SupplyAssetsUsd]: false,
  [COLUMN_IDS.SizeUsd]: false,
  [COLUMN_IDS.OraclePrice]: false,
  [COLUMN_IDS.IrmAddress]: false,
  [COLUMN_IDS.BorrowToTarget]: false,
  [COLUMN_IDS.ApyAtTarget]: false,
  [COLUMN_IDS.UniqueKey]: false,
  [COLUMN_IDS.BadDebt]: false,
  [COLUMN_IDS.RealizedBadDebt]: false
}

export const MARKETS_INITIAL_SORTING: { id: string; desc: boolean }[] = [
  {
    id: COLUMN_IDS.BorrowAssetsUsd,
    desc: true
  }
]

export const MARKETS_STORAGE_KEY = 'markets'
// v12: Lend Rate was added as a default visible market column.
// v11: Loan column moved ahead of Collateral (VRM-5356 loan-first market identity).
// Bumped so users with a persisted collateral-first columnOrder pick up the new default:
// reordering any column writes the full list, which would otherwise pin them to the old
// order for good. `validateTableSettings` has no per-version migration, so the bump also
// resets column visibility, sorting and filters once. Accepted over a migration hook,
// which must land in the helper shared by every table.
export const MARKETS_STORAGE_VERSION = 12

export const MARKETS_DEFAULT_TABLE_SETTINGS: TableSettingsState = {
  columnVisibility: MARKETS_INITIAL_COLUMNS_VISIBILITY,
  sorting: MARKETS_INITIAL_SORTING,
  columnFilters: [],
  columnOrder: [],
  version: MARKETS_STORAGE_VERSION
}

export const SORT_BY_OPTIONS: SortByOption<string>[] = [
  { value: COLUMN_IDS.LoanAssetSymbol, label: 'Loan asset' },
  { value: COLUMN_IDS.CollateralAssetSymbol, label: 'Collateral asset' },
  { value: COLUMN_IDS.Lltv, label: 'LLTV' },
  { value: COLUMN_IDS.BorrowAssetsUsd, label: 'Outstanding Loans' },
  { value: COLUMN_IDS.TotalLiquidityUsd, label: 'Total Liquidity' },
  {
    value: COLUMN_IDS.AvgNetBorrowApy,
    label: `Borrow Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`
  },
  {
    value: COLUMN_IDS.NetSupplyApy,
    label: `Lend Rate (${AVG_APY_DURATION_LABEL.toLowerCase()})`
  },
  { value: COLUMN_IDS.Utilization, label: 'Utilization' },
  { value: COLUMN_IDS.LiquidityAssets, label: 'Market Liquidity' },
  { value: COLUMN_IDS.CollateralAssetsUsd, label: 'Total Collateral' },
  { value: COLUMN_IDS.NetBorrowApy, label: 'Instant Rate' },
  { value: COLUMN_IDS.WeeklyBorrowApy, label: '7D Rate' },
  { value: COLUMN_IDS.SupplyAssetsUsd, label: 'Total Supply' },
  { value: COLUMN_IDS.SizeUsd, label: 'Total Market Size' },
  { value: COLUMN_IDS.OraclePrice, label: 'Collateral Price' },
  { value: COLUMN_IDS.BorrowToTarget, label: 'Borrow to Target' },
  { value: COLUMN_IDS.ApyAtTarget, label: 'Rate at Target' },
  { value: COLUMN_IDS.BadDebt, label: 'Bad Debt' },
  { value: COLUMN_IDS.RealizedBadDebt, label: 'Realized Bad Debt' }
]

export const MarketsUrlFilterParser = {
  search: parseAsString.withDefault(''),
  chains: parseAsArrayOf(parseAsInteger).withDefault([]),
  collateralAssetIdsFilter: parseAsArrayOf(parseAsString).withDefault([]),
  loanAssetIdsFilter: parseAsArrayOf(parseAsString).withDefault([]),
  curators: parseAsArrayOf(parseAsString).withDefault([]),
  sizeUsd_gte: parseAsFloat,
  sizeUsd_lte: parseAsFloat,
  totalLiquidityUsd_gte: parseAsFloat,
  totalLiquidityUsd_lte: parseAsFloat,
  avgNetBorrowApy_gte: parseAsFloat,
  avgNetBorrowApy_lte: parseAsFloat,
  lltv_gte: parseAsFloat,
  lltv_lte: parseAsFloat
}

export const MarketsUrlFilterCache = createSearchParamsCache(MarketsUrlFilterParser)
